/**
 * iter280 — Prévisualisation / validation de la lecture d'une carte grise,
 * puis création du client + véhicule + OR.
 * L'analyse (Claude Vision) est faite par le parent et passée dans `analyse`.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Loader2, CheckCircle2, UserCheck, Car, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const PAYS_LABELS = { CH: "🇨🇭 Suisse", FR: "🇫🇷 France", DE: "🇩🇪 Allemagne", IT: "🇮🇹 Italie", AT: "🇦🇹 Autriche" };

const Champ = ({ label, value, onChange, required, placeholder, type = "text", testid, autoFocus }) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-600">
      {label}{required && <span className="text-red-500"> *</span>}
    </span>
    <Input
      value={value || ""}
      type={type}
      autoFocus={autoFocus}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="h-9 mt-0.5"
      data-testid={testid}
    />
  </label>
);

export default function CarteGriseOrDialog({ open, photoId, analyse, onClose }) {
  const navigate = useNavigate();
  const [client, setClient] = useState({});
  const [vehicule, setVehicule] = useState({});
  const [clientId, setClientId] = useState(null);
  const [vehiculeId, setVehiculeId] = useState(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!analyse) return;
    const f = analyse.fields || {};
    const cExist = analyse.client;
    const vExist = analyse.vehicule;
    setClientId(cExist?.id || null);
    setVehiculeId(vExist?.id || null);
    setClient({
      type: (cExist?.type === "entreprise" || (!cExist && f.titulaire_raison_sociale)) ? "entreprise" : "particulier",
      raison_sociale: cExist?.raison_sociale || f.titulaire_raison_sociale || "",
      nom: cExist?.nom || f.titulaire_nom || "",
      prenom: cExist?.prenom || f.titulaire_prenom || "",
      adresse: cExist?.adresse || f.titulaire_rue || "",
      npa: cExist?.npa || f.titulaire_npa || "",
      localite: cExist?.localite || f.titulaire_ville || "",
      pays: cExist?.pays || f.pays || "CH",
      tel_principal: cExist?.tel_principal || "",
      email: cExist?.email || "",
    });
    setVehicule({
      marque: vExist?.marque || f.marque || "",
      modele: vExist?.modele || f.modele || "",
      plaque: vExist?.plaque || f.plaque || "",
      vin: vExist?.vin || f.vin || "",
      premiere_mec: vExist?.premiere_mec || f.premiere_mec || "",
      couleur: vExist?.couleur || f.couleur || "",
      carburant: vExist?.carburant || f.carburant || "",
      km: f.kilometrage ? String(f.kilometrage) : "",
    });
  }, [analyse]);

  const choisirCandidat = (c) => {
    setClientId(c.id);
    setClient((prev) => ({
      ...prev,
      tel_principal: prev.tel_principal || c.tel_principal || "",
      email: prev.email || c.email || "",
      npa: prev.npa || c.npa || "",
      localite: prev.localite || c.localite || "",
    }));
    toast.success(`Client existant retenu : ${c.label}`);
  };

  const creer = async () => {
    if (!String(vehicule.km || "").trim()) { toast.error("Le kilométrage est requis"); return; }
    setCreating(true);
    try {
      const { data } = await api.post(`/photos/${photoId}/carte-grise/creer-or`, {
        client, vehicule, client_id: clientId, vehicule_id: vehiculeId,
      });
      toast.success(`OR ${data.or_numero} créé`);
      onClose();
      navigate(`/atelier/or/${data.or_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Création impossible");
    } finally {
      setCreating(false);
    }
  };

  const f = analyse?.fields || {};
  const confiance = Math.round((f.confiance || 0) * 100);
  const candidats = analyse?.client_candidats || [];
  const estEntreprise = client.type === "entreprise";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="cg-or-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            Carte grise lue — vérifiez avant de créer l&apos;OR
            {f.pays && <Badge variant="outline" className="bg-gray-50" data-testid="cg-or-pays">{PAYS_LABELS[f.pays] || f.pays}</Badge>}
            <Badge
              variant="outline"
              className={confiance >= 70 ? "bg-[#52a675]/15 text-[#2d5f3f] border-[#52a675]/40" : "bg-amber-50 text-amber-700 border-amber-200"}
              data-testid="cg-or-confiance"
            >
              Lecture {confiance}%
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Complétez le téléphone / e-mail (optionnels) et le kilométrage, puis validez.
            Les champs laissés vides n&apos;ont pas été lus de façon sûre : rien n&apos;est deviné.
          </DialogDescription>
        </DialogHeader>

        {confiance < 70 && (
          <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5" data-testid="cg-or-alerte">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            Lecture incertaine : contrôlez bien la plaque, le VIN et la date de mise en circulation.
          </div>
        )}

        {/* Correspondances trouvées */}
        {(vehiculeId || clientId) && (
          <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="cg-or-matchs">
            {vehiculeId && (
              <Badge variant="outline" className="bg-[#52a675]/10 text-[#2d5f3f] border-[#52a675]/40">
                <Car className="w-3 h-3 mr-1" /> Véhicule déjà au fichier
              </Badge>
            )}
            {clientId && (
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                <UserCheck className="w-3 h-3 mr-1" /> Client déjà au fichier
              </Badge>
            )}
            {clientId && analyse?.client_motif && (
              <span className="text-[11px] text-gray-500" data-testid="cg-or-client-motif">
                {analyse.client_motif}
              </span>
            )}
          </div>
        )}

        {!clientId && candidats.length > 0 && (
          <div className="border border-blue-200 bg-blue-50/60 rounded-lg p-3" data-testid="cg-or-candidats">
            <div className="text-xs font-semibold text-blue-900 mb-2">
              Plusieurs clients peuvent correspondre — choisissez, ou laissez pour en créer un nouveau :
            </div>
            <div className="flex flex-wrap gap-2">
              {candidats.map((c) => (
                <Button
                  key={c.id} size="sm" variant="outline" className="h-8 bg-white"
                  onClick={() => choisirCandidat(c)}
                  data-testid={`cg-or-candidat-${c.id}`}
                >
                  {c.label} {c.npa ? `· ${c.npa} ${c.localite || ""}` : ""}
                  {c.adresse ? ` · ${c.adresse}` : ""}
                </Button>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Client */}
          <section className="space-y-2.5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              Client
              {!clientId && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Nouveau</Badge>}
            </h3>
            <div className="flex gap-2">
              {["particulier", "entreprise"].map((t) => (
                <Button
                  key={t} size="sm" variant={client.type === t ? "default" : "outline"}
                  className={`h-7 text-xs ${client.type === t ? "bg-[#52a675] hover:bg-[#3d8a5c]" : ""}`}
                  onClick={() => setClient({ ...client, type: t })}
                  data-testid={`cg-or-client-type-${t}`}
                >
                  {t === "particulier" ? "Particulier" : "Entreprise"}
                </Button>
              ))}
            </div>
            {estEntreprise ? (
              <Champ label="Raison sociale" required value={client.raison_sociale} onChange={(v) => setClient({ ...client, raison_sociale: v })} testid="cg-or-client-raison-sociale" />
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <Champ label="Nom" required value={client.nom} onChange={(v) => setClient({ ...client, nom: v })} testid="cg-or-client-nom" />
                <Champ label="Prénom" value={client.prenom} onChange={(v) => setClient({ ...client, prenom: v })} testid="cg-or-client-prenom" />
              </div>
            )}
            <Champ label="Adresse" value={client.adresse} onChange={(v) => setClient({ ...client, adresse: v })} testid="cg-or-client-adresse" />
            <div className="grid grid-cols-3 gap-2">
              <Champ label="NPA" value={client.npa} onChange={(v) => setClient({ ...client, npa: v })} testid="cg-or-client-npa" />
              <div className="col-span-2">
                <Champ label="Localité" value={client.localite} onChange={(v) => setClient({ ...client, localite: v })} testid="cg-or-client-localite" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Champ label="Téléphone" placeholder="à compléter" value={client.tel_principal} onChange={(v) => setClient({ ...client, tel_principal: v })} testid="cg-or-client-tel" />
              <Champ label="E-mail" placeholder="à compléter" value={client.email} onChange={(v) => setClient({ ...client, email: v })} testid="cg-or-client-email" />
            </div>
          </section>

          {/* Véhicule */}
          <section className="space-y-2.5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              Véhicule
              {!vehiculeId && <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">Nouveau</Badge>}
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <Champ label="Marque" required value={vehicule.marque} onChange={(v) => setVehicule({ ...vehicule, marque: v })} testid="cg-or-veh-marque" />
              <Champ label="Modèle" required value={vehicule.modele} onChange={(v) => setVehicule({ ...vehicule, modele: v })} testid="cg-or-veh-modele" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Champ label="Plaque" required value={vehicule.plaque} onChange={(v) => setVehicule({ ...vehicule, plaque: v.toUpperCase() })} testid="cg-or-veh-plaque" />
              <Champ label="VIN" value={vehicule.vin} onChange={(v) => setVehicule({ ...vehicule, vin: v.toUpperCase() })} testid="cg-or-veh-vin" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Champ label="1ère mise en circulation" type="date" value={vehicule.premiere_mec} onChange={(v) => setVehicule({ ...vehicule, premiere_mec: v })} testid="cg-or-veh-mec" />
              <Champ label="Couleur" value={vehicule.couleur} onChange={(v) => setVehicule({ ...vehicule, couleur: v })} testid="cg-or-veh-couleur" />
            </div>
            <div className="rounded-lg border border-[#52a675]/40 bg-[#52a675]/5 p-2.5">
              <Champ
                label="Kilométrage au compteur" required autoFocus
                placeholder="à relever sur le véhicule"
                value={vehicule.km}
                onChange={(v) => setVehicule({ ...vehicule, km: v.replace(/\D/g, "") })}
                testid="cg-or-veh-km"
              />
            </div>
          </section>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose} data-testid="cg-or-cancel">Annuler</Button>
          <Button
            onClick={creer}
            disabled={creating}
            className="bg-[#52a675] hover:bg-[#3d8a5c]"
            data-testid="cg-or-create"
          >
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Valider et créer l&apos;OR
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
